/**
 * AccordionLayout Component
 * Renders accordion-style layout (collapsible panels)
 */

import React, { ReactNode, useState } from 'react';
import './AccordionLayout.css';
import { FormContainerConfig } from '../types/index';
import { ValidationBadge } from './ValidationBadge';
import { ChevronDownIcon, ChevronRightIcon } from '@atlas-forms/ui-components-react';

export interface AccordionLayoutProps {
  config: FormContainerConfig;
  activeIndex: number;
  onTabChange: (index: number) => void;
  errors: Record<string, any[]>;
  children: ReactNode;
  theme?: 'dark' | 'light';
}

/**
 * Accordion layout component
 * Each form is a collapsible panel
 */
export const AccordionLayout: React.FC<AccordionLayoutProps> = ({
  config,
  activeIndex,
  onTabChange,
  errors,
  children,
  theme = 'light',
}) => {
  return (
    <div className="af-accordion-layout">
      {config.childForms.map((child, index) => {
        const errorCount = (errors[child.formID] || []).length;
        const isActive = index === activeIndex;

        return (
          <div key={child.formID} className="af-accordion-layout__item">
            <button
              type="button"
              className={`af-accordion-layout__header${isActive ? ' af-accordion-layout__header--active' : ''}`}
              onClick={() => onTabChange(index)}
              disabled={config.validationMode === 'blocked' && index > activeIndex}
            >
              <div className="af-accordion-layout__header-content">
                <span className="af-accordion-layout__icon">
                  {isActive ? <ChevronDownIcon width={12} height={12} /> : <ChevronRightIcon width={12} height={12} />}
                </span>
                <span>{child.label}</span>
              </div>
              <ValidationBadge count={errorCount} />
            </button>

            <div className={`af-accordion-layout__content${isActive ? ' af-accordion-layout__content--active' : ''}`}>
              {isActive ? children : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

AccordionLayout.displayName = 'AccordionLayout';
